import { useQueries, useQuery } from '@tanstack/react-query'

import {
  customerSavingsAccountsQueryOptions,
  groupLoanAccountsQueryOptions,
} from '@/api/collections-api'
import type { CollectionSavingsAccount } from '@/api/collections-types'
import { businessDayQueryOptions } from '@/api/reference-api'
import { session } from '@/api/session'
import type { GroupMember } from '@/api/types'
import { useAuth } from '@/auth/use-auth'

/**
 * The reads a collection sheet is assembled from.
 *
 * The group, its members and its meeting calendar come from `@/features/groups/queries` rather
 * than being re-fetched here, so a group opened from a sheet and the same group opened from the
 * group list share one cache entry. What is added here is everything the sheet needs that the
 * group does not carry: the loan rounds written under it, each member's savings accounts, and the
 * branch's open business day — which is the date every one of these postings will land on.
 */

/**
 * The loan rounds written under a group.
 *
 * Disabled rather than attempted when the reader does not hold `loans.view`: a 403 on every
 * collection sheet is noise in the log and tells the operator nothing the screen cannot say for
 * itself. The distinction between "you may not see this" and "there is nothing here" is made in
 * the sheet model and is rendered as two different things.
 */
export const useGroupLoanAccounts = (groupId: string, enabled: boolean) =>
  useQuery(groupLoanAccountsQueryOptions(groupId, enabled))

/**
 * Each member's savings and security-deposit accounts, keyed by customer.
 *
 * One request per member, because `/savings/accounts` has no group filter — the same gap the group
 * members panel works around when it reads each member's KYC state. A village bank is a few dozen
 * people, so this is a few dozen requests against a cache that then serves the member's own screen
 * for nothing; it is not a page of ten thousand rows.
 *
 * The map is built inside `combine` rather than in a `useMemo` over the returned array, so its
 * identity is stable while the underlying queries are. That matters more here than it looks: the
 * sheet is rebuilt from this map, and a map with a new identity on every render would rebuild
 * sixty rows of money fields on every keystroke.
 *
 * A member still being read is left out of the map entirely, which the sheet model reads as "not
 * answered yet" rather than as "no accounts" — the same absent-versus-null distinction the cells
 * render differently.
 */
export function useMemberSavingsMap(
  members: GroupMember[],
  enabled: boolean,
): Map<string, CollectionSavingsAccount[] | null> {
  return useQueries({
    queries: members.map((member) => ({
      ...customerSavingsAccountsQueryOptions(member.customerId, enabled),
    })),
    combine: (results) => {
      const map = new Map<string, CollectionSavingsAccount[] | null>()

      results.forEach((result, index) => {
        const customerId = members[index]?.customerId
        // A member still in flight is left out of the map entirely. `null` is reserved for a read
        // that was attempted and failed, which the sheet renders as a warning rather than as "no
        // accounts" — the two are not the same fact and must not look alike.
        if (customerId === undefined || result.isPending) {
          return
        }

        map.set(customerId, result.data?.items ?? null)
      })

      return map
    },
  })
}

/**
 * The branch's open business day.
 *
 * This is the date every posting on the sheet lands on, and it is not the browser's date: a branch
 * that has not closed yesterday is still working in yesterday. `acceptsPostings` is sent rather
 * than derived, and this screen refuses to post when it is false — a sheet keyed against a closed
 * day is forty refusals in a row.
 */
export function useCollectionBusinessDay() {
  const { user } = useAuth()
  const branchId = session.getBranchId() ?? user?.homeBranchId ?? null

  return useQuery(businessDayQueryOptions(branchId))
}
